// apps/client/src/lib/adsgram.ts
//
// AdsGram SDK Wrapper — Multi-Block Support
// Follows official docs: https://docs.adsgram.ai/publisher/
//
// Reward & Interstitial → window.Adsgram.init() + show()
// Task → <adsgram-task> web component (handled separately)
//

/** Ad placement identifiers for Reward & Interstitial ONLY */
export type AdPlacement =
    | 'store_chips'     // StorePage: Watch ad → +50 chips     [Reward]
    | 'game_single'     // GamePage:  Watch 1 ad → +1 card     [Reward]
    | 'game_bundle'     // GamePage:  Watch 3 ads → +5 cards   [Reward]
    | 'postgame'        // GamePage:  Post-game                [Interstitial]
    | 'fortune_wheel_spin';

// NOTE: mission_task uses <adsgram-task> web component — NOT init()/show()

/** Block ID map — loaded from VITE_AD_* env vars */
const BLOCK_IDS: Record<AdPlacement, string> = {
    store_chips: import.meta.env['VITE_AD_STORE_CHIPS'] ?? '22396',
    game_single: import.meta.env['VITE_AD_GAME_SINGLE'] ?? '22727',
    game_bundle: import.meta.env['VITE_AD_GAME_BUNDLE'] ?? '22728',
    postgame: import.meta.env['VITE_AD_POSTGAME'] ?? 'int-22730',
    fortune_wheel_spin: import.meta.env['VITE_AD_STORE_CHIPS'] ?? '22396',
};

/** Task block ID — used by <AdsgramTask> component */
export const TASK_BLOCK_ID: string = import.meta.env['VITE_AD_MISSION_TASK'] ?? 'task-22729';

/** Result from showing an ad (matches official Adsgram API) */
export interface ShowPromiseResult {
    done: boolean;        // true if user watched till the end
    description: string;  // event description
    state: 'load' | 'render' | 'playing' | 'destroy';
    error: boolean;       // true if error occurred
}

/** Controller for ad operations */
interface AdController {
    show(): Promise<ShowPromiseResult>;
    destroy(): void;
}

/** Init params */
interface AdsgramInitParams {
    blockId: string;
    debug?: boolean;
    debugConsole?: boolean;
    debugBannerType?: 'FullscreenMedia' | 'RewardedVideo';
}

declare global {
    interface Window {
        Adsgram?: {
            init(params: AdsgramInitParams): AdController;
        };
        Telegram?: {
            WebApp: any;
        };
    }
}

/** Cache controllers per block ID (docs: same blockId → same AdController) */
const controllers = new Map<string, AdController>();

/**
 * Shows a Reward or Interstitial ad for a specific placement.
 *
 * Per Adsgram docs:
 *   - Promise RESOLVES when user watches ad till the end (or closes interstitial)
 *   - Promise REJECTS when user gets error or skips
 *
 * @returns true if ad was completed, false if skipped/error
 */
export async function showAd(placement: AdPlacement): Promise<boolean> {
    if (!window.Adsgram) {
        throw new Error('AdsGram SDK not loaded. Ensure sad.min.js is in index.html <head>.');
    }

    const blockId = BLOCK_IDS[placement];

    // Reuse existing controller for this block, or create new one
    if (!controllers.has(blockId)) {
        controllers.set(blockId, window.Adsgram.init({
            blockId,
        }));
    }

    try {
        // show() resolves = ad completed, rejects = skip/error
        await controllers.get(blockId)!.show();
        return true;
    } catch {
        // User skipped or ad error — no reward
        return false;
    }
}

/**
 * Checks if the AdsGram SDK script is loaded
 */
export function isAdsgramReady(): boolean {
    return typeof window.Adsgram !== 'undefined';
}

/**
 * Gets the block ID for a specific placement (for debugging/admin)
 */
export function getBlockId(placement: AdPlacement): string {
    return BLOCK_IDS[placement];
}
